import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

const InventoryReportPrint = ({ data, depoName, wasteLogs = [], onClose }) => {
    const { t, i18n } = useTranslation();
    const contentRef = useRef(null);

    const totalPhy = data.reduce((acc, curr) => acc + parseFloat(curr.quantity || curr.total_quantity || 0), 0);
    const totalVal = data.reduce((acc, curr) => acc + (parseFloat(curr.quantity || curr.total_quantity || 0) * parseFloat(curr.purchase_price || curr.price || 0)), 0);
    const totalWasteVal = wasteLogs.reduce((acc, curr) => acc + parseFloat(curr.loss_cost || 0), 0);
    const subTotal = totalVal - totalWasteVal;
    const deduction19 = subTotal * 0.19;
    const subTotalAfterDeduction = subTotal - deduction19;

    const handlePrint = () => {
        if (!contentRef.current) return;
        const html = `<!DOCTYPE html><html><head>
            <meta charset="utf-8"><title>Stock Inventory Report — ${depoName || 'All Branches'}</title>
            <style>
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 20px; }
                @page { size: A4 landscape; margin: 10mm; }
                table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
                th { padding: 10px 14px; font-size: 11px; font-weight: 900; text-transform: uppercase; color: #6b7280; background: #f9fafb; text-align: left; }
                td { padding: 10px 14px; font-size: 11px; border-bottom: 1px solid #f1f5f9; }
                .flex { display: flex; } .justify-between { justify-content: space-between; } .items-start { align-items: flex-start; } .items-center { align-items: center; }
                .justify-end { justify-content: flex-end; } .justify-around { justify-content: space-around; }
                .gap-4 { gap: 16px; } .mb-8 { margin-bottom: 32px; } .mb-12 { margin-bottom: 48px; } .mb-16 { margin-bottom: 64px; } .mt-2 { margin-top: 8px; } .mt-6 { margin-top: 24px; }
                .pb-6 { padding-bottom: 24px; } .pt-8 { padding-top: 32px; } .p-6 { padding: 24px; } .px-4 { padding: 0 14px; } .py-3 { padding: 10px 0; } .pt-1 { padding-top: 4px; }
                .text-left { text-align: left; } .text-right { text-align: right; } .text-center { text-align: center; }
                .font-bold { font-weight: 700; } .font-black { font-weight: 900; } .font-mono { font-family: monospace; }
                .uppercase { text-transform: uppercase; } .tracking-widest { letter-spacing: 0.1em; }
                .text-xs { font-size: 10px; } .text-sm { font-size: 12px; } .text-lg { font-size: 16px; } .text-2xl { font-size: 22px; } .text-xl { font-size: 18px; } .text-8px { font-size: 8px; }
                .border-b-2 { border-bottom: 2px solid #312e81; } .border-l-4 { border-left: 4px solid #4f46e5; } .border-t { border-top: 1px solid #e5e7eb; }
                .text-indigo-900 { color: #312e81; } .text-indigo-800 { color: #3730a3; } .text-gray-400 { color: #9ca3af; } .text-gray-300 { color: #d1d5db; } .text-gray-800 { color: #1f2937; } .text-gray-500 { color: #6b7280; }
                .text-indigo-600 { color: #4f46e5; } .text-red-400 { color: #f87171; } .text-gray-900 { color: #111827; }
                .bg-indigo-50 { background: #eef2ff; } .border-l-4 { border-left: 4px solid #4f46e5; }
                .w-64 { width: 256px; } .w-40 { width: 160px; } .w-16 { width: 64px; } .w-20 { width: 80px; }
                .rounded-lg { border-radius: 8px; } .rtl\\:text-right { } 
                img { max-width: 60px; object-fit: contain; }
                .page-break-avoid { page-break-inside: avoid; }
                .no-print { display: none !important; }
                .section-title { font-size: 14px; font-weight: 900; color: #1e3a8a; margin: 20px 0 10px 0; text-transform: uppercase; border-left: 4px solid #1e3a8a; padding-left: 10px; }
            </style>
        </head><body>${contentRef.current.outerHTML}
        <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
        </body></html>`;
        const popup = window.open('', '_blank', 'width=950,height=750,scrollbars=yes');
        if (popup) { popup.document.write(html); popup.document.close(); }
        else alert('Please allow popups for this site to print.');
    };

    return (
        <div ref={contentRef} className="fixed inset-0 z-[100] bg-white overflow-y-auto p-8 font-sans print:p-0 print:m-0 print:static print:bg-transparent print-report-area">
            {/* Action Buttons (Hidden on Print) */}
            <div className="flex justify-end items-center gap-3 mb-8 print:hidden no-print">
                <div className="flex items-center bg-gray-100 border border-gray-300 rounded-lg p-0.5 shadow-sm">
                    <button
                        type="button"
                        onClick={() => i18n.changeLanguage('fr')}
                        className={`px-3 py-1 text-xs font-bold rounded transition-colors flex items-center gap-1 ${
                            (i18n.language || 'en').startsWith('fr')
                                ? 'bg-amber-500 text-white shadow-xs'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                        <span>🇫🇷</span> FR
                    </button>
                    <button
                        type="button"
                        onClick={() => i18n.changeLanguage('en')}
                        className={`px-3 py-1 text-xs font-bold rounded transition-colors flex items-center gap-1 ${
                            (i18n.language || 'en').startsWith('en')
                                ? 'bg-indigo-600 text-white shadow-xs'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                        <span>🇬🇧</span> EN
                    </button>
                </div>
                <button
                    onClick={handlePrint}
                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg shadow hover:bg-indigo-700 transition font-bold cursor-pointer"
                >
                    {t('Print PDF Report')}
                </button>
                <button
                    onClick={onClose}
                    className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 transition font-bold cursor-pointer"
                >
                    {t('Close')}
                </button>
            </div>

            {/* A4 Content Container landscape */}
            <div className="max-w-6xl mx-auto border p-12 bg-white shadow-sm print:border-none print:shadow-none print:p-0 print:max-w-full print-report-content overflow-x-auto print:overflow-visible">

                {/* Header */}
                <div className="flex justify-between items-start border-b-2 border-indigo-900 pb-6 mb-8">
                    <div className="flex items-center gap-4">
                        <img src="/logo.png" alt="Company Logo" className="w-16 h-16 object-contain" />
                        <div>
                            <h1 className="text-2xl font-black text-indigo-900 uppercase">{t('Stock Inventory Report')}</h1>
                            <p className="text-xs text-gray-400 font-bold tracking-widest uppercase">{t('Warehouse Management System')}</p>
                        </div>
                    </div>
                    <div className="text-right rtl:text-left">
                        <p className="text-[10px] text-gray-400 uppercase font-black">{t('Location Filter')}</p>
                        <p className="text-lg font-bold text-indigo-800">{depoName ? t(depoName) : t('All Depos (Consolidated)')}</p>
                        <p className="text-[10px] text-gray-300 mt-2">{t('Generated on:')} {new Date().toLocaleString()}</p>
                    </div>
                </div>

                <div className="section-title">{t('Current Inventory Levels')}</div>
                {/* Table */}
                <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left text-xs border-collapse divide-y divide-gray-100 mb-8">
                    <thead className="bg-gray-50 uppercase font-black text-gray-500 tracking-tighter">
                        <tr>
                            <th className="px-4 py-3 rtl:text-right">{t('Product Name')}</th>
                            <th className="px-4 py-3 text-center">{t('Reference')}</th>
                            <th className="px-4 py-3 rtl:text-right">{t('Category')}</th>
                            <th className="px-4 py-3 text-right rtl:text-left">{t('Stock Level')}</th>
                            <th className="px-4 py-3 text-right rtl:text-left">{t('Waste')}</th>
                            <th className="px-4 py-3 text-right rtl:text-left">{t('Loss %')}</th>
                            <th className="px-4 py-3 text-right rtl:text-left">{t('Price')}</th>
                            <th className="px-4 py-3 text-right rtl:text-left">{t('Value')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {data.map((item, idx) => {
                            const stock = parseFloat(item.quantity || item.total_quantity || 0);
                            const waste = parseFloat(item.waste_quantity || 0);
                            const total = stock + waste;
                            const percent = total > 0 ? (waste / total) * 100 : 0;
                            const price = parseFloat(item.purchase_price || item.price || 0);
                            return (
                                <tr key={idx} className="hover:bg-gray-50 transition">
                                    <td className="px-4 py-3 font-bold text-gray-800">{item.name || item.product_name}</td>
                                    <td className="px-4 py-3 text-center font-mono text-gray-400">{item.sku}</td>
                                    <td className="px-4 py-3 text-gray-500">{t(item.category) || t('Uncategorized')}</td>
                                    <td className={`px-4 py-3 text-right rtl:text-left font-black ${stock > 0 ? 'text-indigo-600' : 'text-red-400'}`}>
                                        {stock.toFixed(0)}
                                    </td>
                                    <td className="px-4 py-3 text-right rtl:text-left font-bold text-red-500">
                                        {waste.toFixed(0)}
                                    </td>
                                    <td className="px-4 py-3 text-right rtl:text-left font-black text-orange-600">
                                        {percent.toFixed(1)}%
                                    </td>
                                    <td className="px-4 py-3 text-right rtl:text-left text-gray-400">{price.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right rtl:text-left font-bold">{ (stock * price).toFixed(2) }</td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className="bg-indigo-50 font-black">
                            <td colSpan="3" className="px-4 py-3 uppercase text-indigo-900">{t('Summary Totals')}</td>
                            <td className="px-4 py-3 text-right text-indigo-900 font-black">{totalPhy.toFixed(0)}</td>
                            <td className="px-4 py-3 text-right text-red-500 font-black">{data.reduce((a,c)=>a+parseFloat(c.waste_quantity||0),0).toFixed(0)}</td>
                            <td></td>
                            <td></td>
                            <td className="px-4 py-3 text-right text-indigo-900 font-black">{totalVal.toFixed(2)} CFA</td>
                        </tr>
                    </tfoot>
                </table>
</div>

                <div className="section-title">{t('Detailed Waste / Loss Log')}</div>
                <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left text-xs border-collapse divide-y divide-gray-100 mb-12">
                    <thead className="bg-red-50 uppercase font-black text-red-800 tracking-tighter">
                        <tr>
                            <th className="px-4 py-3">{t('Product Name')}</th>
                            <th className="px-4 py-3 text-center">{t('Qty Lost')}</th>
                            <th className="px-4 py-3 text-center">{t('Reason')}</th>
                            <th className="px-4 py-3 text-right">{t('Loss Value')}</th>
                            <th className="px-4 py-3 text-right">{t('19% Deduct')}</th>
                            <th className="px-4 py-3 text-right">{t('Sub Price')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {wasteLogs.length === 0 ? (
                            <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400 italic">{t('No waste logs recorded for this period')}</td></tr>
                        ) : wasteLogs.map((w, i) => {
                            const loss = parseFloat(w.loss_cost || 0);
                            return (
                                <tr key={i}>
                                    <td className="px-4 py-3 font-bold text-gray-800">{w.product_name}</td>
                                    <td className="px-4 py-3 text-center">{w.quantity}</td>
                                    <td className="px-4 py-3 text-center text-red-600">{w.reason}</td>
                                    <td className="px-4 py-3 text-right font-bold">CFA{loss.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right text-red-700">CFA{(loss * 0.19).toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right text-blue-700 font-bold">CFA{(loss * 0.81).toFixed(2)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
</div>

                {/* Summary Section */}
                <div className="flex justify-end rtl:justify-start mb-16 page-break-avoid">
                    <div className="w-80 bg-slate-50 p-6 rounded-lg border-l-4 border-indigo-600 rtl:border-r-4 rtl:border-l-0">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{t('Gross Stock Value')}</span>
                            <span className="text-sm font-mono font-bold">CFA{totalVal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2 text-red-600">
                            <span className="text-[10px] font-black uppercase tracking-widest">{t('Total Loss (Waste)')}</span>
                            <span className="text-sm font-mono font-bold">- CFA{totalWasteVal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200 text-indigo-900">
                            <span className="text-[10px] font-black uppercase tracking-widest">{t('Subtotal')}</span>
                            <span className="text-lg font-mono font-black">CFA{subTotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center mt-2 text-red-700">
                            <span className="text-[10px] font-black uppercase tracking-widest">{t('19% Deduction')}</span>
                            <span className="text-sm font-mono font-bold">- CFA{deduction19.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center mt-4 pt-4 border-t-2 border-indigo-900 text-indigo-900">
                            <span className="text-[10px] font-black uppercase tracking-widest">{t('Net Total')}</span>
                            <span className="text-xl font-mono font-black">CFA{subTotalAfterDeduction.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                {/* Report Footer */}
                <div className="border-t pt-8 text-center page-break-avoid">
                    <div className="flex justify-around mb-8 opacity-40">
                        <div className="w-40 border-t border-gray-900 pt-1 text-[8px] font-bold uppercase">{t('Warehouse Supervisor')}</div>
                        <div className="w-40 border-t border-gray-900 pt-1 text-[8px] font-bold uppercase">{t('Inventory Control')}</div>
                    </div>
                    <p className="text-[8px] text-gray-300 uppercase tracking-[0.3em] font-black">
                        {t('ERP System')} &bull; {t('Confidential Inventory Log')} &bull; {t('Page 1 of 1')}
                    </p>
                </div>
            </div>

            {/* Print Specific CSS Force */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @media print {
                    html, body {
                        height: auto !important;
                        overflow: visible !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    body * { visibility: hidden !important; }
                    .print-report-area, .print-report-area * { visibility: visible !important; }
                    .print-report-area {
                        position: absolute !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 100% !important;
                        display: block !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        background: white !important;
                        overflow: visible !important;
                        height: auto !important;
                        min-height: 100vh !important;
                    }
                    .print-report-content {
                        border: none !important;
                        box-shadow: none !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 70% !important;
                        max-width: none !important;
                    }
                    @page { size: A4; margin: 10mm; }
                    * { 
                        -webkit-print-color-adjust: exact !important; 
                        print-color-adjust: exact !important; 
                    }
                    /* Scale content down to fit 1 page wide */
                    html {
                        zoom: 0.8;
                    }
                    /* Force tables to not exceed page width */
                    table { 
                        page-break-inside: auto; 
                        width: 100% !important;
                        table-layout: fixed !important;
                        font-size: 10px !important;
                    }
                    th, td {
                        word-wrap: break-word !important;
                        padding: 4px !important;
                    }
                    tr    { page-break-inside: avoid; page-break-after: auto; }
                    thead { display: table-header-group; }
                    tfoot { display: table-footer-group; }
                    .page-break-avoid { page-break-inside: avoid; }
                }
            `}} />
        </div>
    );
};

export default InventoryReportPrint;
